import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import authService from '../../services/keycloak/authService';
import Toast from '../../ui/Toast';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [urlMessage, setUrlMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

  // Display message from URL query params (e.g. after admin removes user)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const message = params.get('message');
    if (message) {
      setToast({ show: true, message: message, type: 'success' }); // URL messages are usually successes or info
      // Clean the URL to remove the message param
      window.history.replaceState({}, document.title, '/login');
    }
  }, [location.search]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError('');
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setToast({ ...toast, show: false });

    try {
      // Validate inputs
      if (!email.trim()) {
        const msg = 'Please enter your email';
        setError(msg);
        setToast({ show: true, message: msg, type: 'error' });
        setIsLoading(false);
        return;
      }
      if (!password.trim()) {
        const msg = 'Please enter your password';
        setError(msg);
        setToast({ show: true, message: msg, type: 'error' });
        setIsLoading(false);
        return;
      }

      // If MFA is required, check for OTP
      if (requiresMfa) {
        if (!otp.trim()) {
          setError('Please enter your OTP code');
          setIsLoading(false);
          return;
        }

        // Try to login with OTP
        await authService.loginWithOtp(email, password, otp, rememberMe);
      } else {
        // Direct login - the backend handles MFA detection automatically
        // If MFA is required, the login function will redirect to Keycloak
        await authService.login(email, password, rememberMe);
      }

      // Save email if remember me is checked
      if (rememberMe) {
        localStorage.setItem('remembered_email', email);
      } else {
        localStorage.removeItem('remembered_email');
      }

      // Navigate immediately after successful login
      // Do NOT add any delay here — the KeycloakContext sync interval will
      // detect the auth change and re-mount this component, resetting state
      const intendedDestination = sessionStorage.getItem('intended_destination');
      if (
        intendedDestination &&
        intendedDestination !== '/login' &&
        intendedDestination !== '/signup'
      ) {
        sessionStorage.removeItem('intended_destination');
        navigate(intendedDestination, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Failed to login. Please check your credentials.');
      setToast({
        show: true,
        message: err.message || 'Failed to login. Please check your credentials.',
        type: 'error',
      });
      console.error('Login error:', err);
      setIsLoading(false);
    }
  };

  const handleOAuthLogin = async (provider) => {
    setIsLoading(true);
    setError('');
    try {
      // Redirect to Keycloak login with Google identity provider hint
      const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8081';
      const realm = process.env.REACT_APP_KEYCLOAK_REALM || 'ai-leadgen';
      const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'ai-leadgen-frontend';
      // Use current origin without encoding for redirect_uri
      const redirectUri = window.location.origin;
      // Store rememberMe preference in sessionStorage to be retrieved after callback
      sessionStorage.setItem('oauth_remember_me', rememberMe ? 'true' : 'false');

      const loginUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid+profile+email&kc_idp_hint=${provider}`;

      window.location.href = loginUrl;
    } catch (err) {
      setError(`Failed to login with ${provider}. Please try again.`);
      setToast({
        show: true,
        message: `Failed to login with ${provider}. Please try again.`,
        type: 'error',
      });
      console.error(`${provider} login error:`, err);
      setIsLoading(false);
    }
  };

  // Check for remembered email
  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem('remembered_email');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  return (
    <div className="auth-container login-split-layout">
      {/* Left Panel - Welcome Section */}
      <div className="auth-left-panel">
        <div className="welcome-content">
          <div className="left-logo-container">
            <img src="/image/Logo.png" alt="AI LeadGen Logo" className="left-auth-logo" />
          </div>

          <div className="welcome-message">
            <h2 className="welcome-subtitle">Sign in to your account</h2>
            <h1 className="welcome-title">WELCOME</h1>
          </div>

          <p className="welcome-description">
            Access your prospect hunting dashboard and unlock powerful lead generation tools.
            Streamline your sales process with intelligent market analysis and comprehensive
            prospect insights.
          </p>
        </div>

        <div className="decorative-elements">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
          <div className="decoration-grid"></div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="auth-right-panel">
        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        )}
        <div className="auth-form-container">
          <div className="auth-header">
            <h1>Login Account</h1>
            <p>Access your prospect hunting dashboard with your credentials</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter your email"
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 3L21 21M10.584 10.587C10.2087 10.9624 9.99778 11.4708 9.99756 12.0013C9.99734 12.5317 10.2078 13.0403 10.5829 13.416C10.958 13.7916 11.4664 14.0025 11.9968 14.0027C12.5273 14.003 13.0359 13.7925 13.4115 13.4174M17.357 17.349C15.726 18.449 13.942 19 12 19C7 19 3.5 15 2 12C2.80712 10.1298 4.0416 8.47059 5.603 7.137M9.363 5.365C10.2204 5.11972 11.1082 4.99684 12 5C17 5 20.5 9 22 12C21.526 13.0452 20.9274 14.0279 20.218 14.927"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {requiresMfa && (
              <div className="form-group">
                <label htmlFor="otp">Authenticator Code</label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={handleOtpChange}
                  placeholder="Enter 6-digit code from your authenticator app"
                  disabled={isLoading}
                  maxLength="6"
                  autoComplete="off"
                />
                <p className="form-hint">
                  Please enter the 6-digit code from your authenticator app (Google Authenticator,
                  Microsoft Authenticator, Authy, etc.)
                </p>
              </div>
            )}

            <div className="form-options">
              {requiresMfa ? (
                <button
                  type="button"
                  onClick={() => {
                    setRequiresMfa(false);
                    setOtp('');
                    setError('');
                  }}
                  disabled={isLoading}
                  className="back-button"
                >
                  ← Back to login
                </button>
              ) : (
                <>
                  <label className="remember-me">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={isLoading}
                    />
                    <span>Keep me signed in</span>
                  </label>
                  <Link to="/forgot-password" className="forgot-password-link">
                    Forgot Password?
                  </Link>
                </>
              )}
            </div>

            <div className="login-terms-notice">
              <p>
                By using our system, you agree to comply with our{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer">
                  Terms & Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>

            <button type="submit" className="auth-button primary" disabled={isLoading}>
              {isLoading ? 'Verifying...' : requiresMfa ? 'Verify OTP' : 'SIGN IN'}
            </button>
          </form>

          <div className="auth-divider">or</div>

          <div className="oauth-buttons">
            <button
              type="button"
              className="oauth-button google"
              onClick={() => handleOAuthLogin('google')}
              disabled={isLoading}
            >
              <svg className="oauth-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span>Continue with Google</span>
            </button>
          </div>

          <div className="auth-footer">
            <p>
              Don't have an account?{' '}
              <Link to="/signup" className="auth-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
